// Savidna CRM - Type Definitions

export type UserRole = string

export type AppointmentStatus =
  | 'programada'
  | 'completada'
  | 'cancelada'
  | 'reprogramada'

export type OpportunityStage =
  | 'contacto_inicial'
  | 'evaluacion'
  | 'propuesta'
  | 'negociacion'
  | 'completado'

export type LeadStatus =
  | 'nuevo'
  | 'contactado'
  | 'calificado'
  | 'cita_programada'
  | 'convertido'
  | 'perdido'

export type LeadSource =
  | 'web'
  | 'telefono'
  | 'referido'
  | 'redes_sociales'
  | 'evento'
  | 'otro'

export type Gender = 'masculino' | 'femenino' | 'otro'

export type PermissionLevel = 'full' | 'read' | 'own' | 'none'

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'complete'
  | 'cancel'
  | 'reschedule'

export interface AuditLog {
  id: string
  timestamp: Date
  userId: string
  userName: string
  action: AuditAction
  module: string
  entityId: string
  entityName: string
  details: string
  changes?: { field: string; oldValue: string; newValue: string }[]
}

export interface FollowUpNote {
  id: string
  userId: string
  userName: string
  contactName: string
  subject: string
  description: string
  createdAt: Date
}

// NEW: Role and Permission interfaces for database
export interface Role {
  id: string
  name: string
  displayName: string
  description: string
  isSystemRole: boolean
  createdAt: Date
  updatedAt: Date
}

export interface Permission {
  id: string
  roleId: string
  module: string
  canCreate: boolean
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  permissionLevel: PermissionLevel
  createdAt: Date
  updatedAt: Date
}

export interface User {
  id: string
  email: string
  password: string
  name: string
  role: UserRole
  avatar?: string
  language: string
  timezone: string
  createdAt: Date
}

export interface Patient {
  id: string
  name: string
  age: number
  gender: Gender
  phone: string
  email: string
  address: string
  emergencyContact: {
    name: string
    relationship: string
    phone: string
  }
  insuranceId: string | null
  medicalHistory: MedicalHistory
  interactions: Interaction[]
  createdAt: Date
}

export interface MedicalHistory {
  initialInterview: {
    background: string
    visitReason: string
    allergies: string
    currentMedications: string
    familyHistory: string
  }
  symptoms: {
    description: string
    duration: string
    severity: string
    notes: string
  }
  physicalExam: {
    bloodPressure: string
    bloodSugar: string
    heartRate: string
    lungs: string
    eyes: string
    throat: string
    abdomen: string
    weight: string
    height: string
    temperature: string
  }
  instruments: string[]
  diagnosis: string
  recommendations: string
  entries: MedicalEntry[]
}

export interface MedicalEntry {
  id: string
  date: Date
  doctorId: string
  doctorName: string
  appointmentId: string
  notes: string
  diagnosis: string
  treatment: string
  followUp: string
}

export interface ClinicalHistory {
  id: string
  patientId: string
  date: Date
  doctorId: string
  doctorName: string
  personalBackground: {
    fullName: string
    birthDate: string
    address: string
    phone: string
    email: string
    age: number
    gender: string
    occupation: string
    numberOfChildren: number
    deliveryType: string
    alcohol: string
    tobacco: string
    drugs: string
    infusions: string
    consultationReason: string
  }
  clinicalBackground: {
    diseases: string
    currentMedications: string
    chronicDiseases: string
    surgicalHistory: string
    implantsOrDevices: string
    implantType: string
    allergies: string
  }
  diagnosis: string
  treatment: string
  medicalIndications: string
  sessions: string
  sessionNumber: string
  comments: string
  doctorSignature: string
  createdAt: Date
  updatedAt: Date
}

export interface Doctor {
  id: string
  name: string
  gender: Gender
  specialtyId: string
  phone: string
  email: string
  schedule: DoctorSchedule
  createdAt: Date
}

export interface DoctorSchedule {
  monday: TimeSlot | null
  tuesday: TimeSlot | null
  wednesday: TimeSlot | null
  thursday: TimeSlot | null
  friday: TimeSlot | null
  saturday: TimeSlot | null
  sunday: TimeSlot | null
}

export interface TimeSlot {
  start: string
  end: string
}

export interface Appointment {
  id: string
  patientId: string
  doctorId: string
  serviceId: string
  date: string
  time: string
  duration: number
  status: AppointmentStatus
  notes: string
  nextAppointmentDate?: string
  nextAppointmentNotes?: string
  createdAt: Date
  updatedAt: Date
  cancelReason?: string
  originalDate?: string
  originalTime?: string
}

export interface Service {
  id: string
  name: string
  duration: number
  price: number
  description: string
  doctorIds?: string[]
}

export interface Insurance {
  id: string
  name: string
  company: string
  coverageType: string
  phone: string
  email: string
}

export interface Specialty {
  id: string
  name: string
  description: string
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string
  company: string
  position: string
  notes: string
  interactions: Interaction[]
  createdAt: Date
  createdBy?: string
}

export interface Company {
  id: string
  name: string
  industry: string
  size: string
  location: string
  phone: string
  email: string
  website?: string
  notes: string
  createdAt: Date
}

export interface Lead {
  id: string
  name: string
  phone: string
  email: string
  source: LeadSource
  status: LeadStatus
  assignedTo: string | null // User ID
  interestedService: string
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface Interaction {
  id: string
  type: 'llamada' | 'correo' | 'reunion' | 'nota'
  date: Date
  description: string
  userId: string
}

export interface Opportunity {
  id: string
  name: string
  contactId: string | null
  companyId?: string | null
  company: string // Deprecated, kept for backward compatibility
  industry: string
  size: string
  location: string
  stage: OpportunityStage
  amount: number
  probability: number
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface SystemNotification {
  id: string
  type:
    | 'appointment_created'
    | 'appointment_cancelled'
    | 'appointment_rescheduled'
    | 'appointment_reminder'
    | 'system'
  title: string
  message: string
  read: boolean
  createdAt: Date
  relatedId?: string
}

// Sistema de Permisos Granular
export interface ModulePermissions {
  dashboard: PermissionLevel
  dashboard_calendar: PermissionLevel
  dashboard_stats: PermissionLevel
  dashboard_pipeline: PermissionLevel
  patients: PermissionLevel
  doctors: PermissionLevel
  appointments: PermissionLevel
  clinicalHistory: PermissionLevel
  services: PermissionLevel
  services_stats: PermissionLevel
  insurance: PermissionLevel
  specialties: PermissionLevel
  contacts: PermissionLevel
  companies: PermissionLevel
  opportunities: PermissionLevel
  followUpNotes: PermissionLevel
  auditLog: PermissionLevel
  settings: PermissionLevel
  settings_profile: PermissionLevel
  settings_preferences: PermissionLevel
  settings_notifications: PermissionLevel
  settings_security: PermissionLevel
  settings_email: PermissionLevel
  settings_users: PermissionLevel
  settings_roles_permissions: PermissionLevel
}

export interface RoleConfig {
  name: string
  description: string
  permissions: ModulePermissions
}

// Configuración de roles predeterminados - solo administrador como base
export const DEFAULT_ROLE_CONFIGS: Record<string, RoleConfig> = {
  administrador: {
    name: 'Administrador',
    description: 'Acceso total al sistema',
    permissions: {
      dashboard: 'full',
      dashboard_calendar: 'full',
      dashboard_stats: 'full',
      dashboard_pipeline: 'full',
      patients: 'full',
      doctors: 'full',
      appointments: 'full',
      clinicalHistory: 'full',
      services: 'full',
      services_stats: 'full',
      insurance: 'full',
      specialties: 'full',
      contacts: 'full',
      companies: 'full',
      opportunities: 'full',
      followUpNotes: 'full',
      auditLog: 'full',
      settings: 'full',
      settings_profile: 'full',
      settings_preferences: 'full',
      settings_notifications: 'full',
      settings_security: 'full',
      settings_email: 'full',
      settings_users: 'full',
      settings_roles_permissions: 'full',
    },
  },
}

// Helper para obtener permisos de un rol (con soporte para permisos personalizados)
export function getRolePermissions(role: UserRole): ModulePermissions {
  // Administrador siempre tiene acceso total
  if (role === 'administrador') {
    return DEFAULT_ROLE_CONFIGS['administrador'].permissions
  }

  // Try to load custom permissions from localStorage
  try {
    const savedPermissions = localStorage.getItem('savidna-role-permissions')
    if (savedPermissions) {
      const customPermissions = JSON.parse(savedPermissions)

      // Check if this role has any permissions configured
      const hasPermsForRole = Object.keys(customPermissions).some(
        (module) => customPermissions[module]?.[role] !== undefined,
      )

      if (hasPermsForRole) {
        const modulePermissions: ModulePermissions = {
          dashboard: customPermissions.dashboard?.[role] || 'none',
          dashboard_calendar:
            customPermissions.dashboard_calendar?.[role] ||
            customPermissions.dashboard?.[role] ||
            'none',
          dashboard_stats:
            customPermissions.dashboard_stats?.[role] ||
            customPermissions.dashboard?.[role] ||
            'none',
          dashboard_pipeline:
            customPermissions.dashboard_pipeline?.[role] ||
            customPermissions.dashboard?.[role] ||
            'none',
          patients: customPermissions.patients?.[role] || 'none',
          doctors: customPermissions.doctors?.[role] || 'none',
          appointments: customPermissions.appointments?.[role] || 'none',
          clinicalHistory: customPermissions.clinicalHistory?.[role] || 'none',
          services: customPermissions.services?.[role] || 'none',
          services_stats:
            customPermissions.services_stats?.[role] ||
            customPermissions.services?.[role] ||
            'none',
          insurance: customPermissions.insurance?.[role] || 'none',
          specialties: customPermissions.specialties?.[role] || 'none',
          contacts: customPermissions.contacts?.[role] || 'none',
          companies: customPermissions.companies?.[role] || 'none',
          opportunities: customPermissions.opportunities?.[role] || 'none',
          followUpNotes: customPermissions.followUpNotes?.[role] || 'none',
          auditLog: customPermissions.auditLog?.[role] || 'none',
          settings: customPermissions.settings?.[role] || 'none',
          // Settings sub-modules: use ONLY their own explicit value, NO fallback to parent
          settings_profile:
            customPermissions.settings_profile?.[role] || 'none',
          settings_preferences:
            customPermissions.settings_preferences?.[role] || 'none',
          settings_notifications:
            customPermissions.settings_notifications?.[role] || 'none',
          settings_security:
            customPermissions.settings_security?.[role] || 'none',
          settings_email: customPermissions.settings_email?.[role] || 'none',
          settings_users: customPermissions.settings_users?.[role] || 'none',
          settings_roles_permissions:
            customPermissions.settings_roles_permissions?.[role] || 'none',
        }
        return modulePermissions
      }
    }
  } catch (e) {
    console.error('Error loading custom permissions:', e)
  }

  // For roles without configured permissions, give NO access (must configure first)
  // But give profile access by default so everyone can see their own profile
  return {
    dashboard: 'read',
    dashboard_calendar: 'read',
    dashboard_stats: 'none',
    dashboard_pipeline: 'none',
    patients: 'none',
    doctors: 'none',
    appointments: 'none',
    clinicalHistory: 'none',
    services: 'none',
    services_stats: 'none',
    insurance: 'none',
    specialties: 'none',
    contacts: 'none',
    companies: 'none',
    opportunities: 'none',
    followUpNotes: 'none',
    auditLog: 'none',
    settings: 'read',
    settings_profile: 'full',
    settings_preferences: 'full',
    settings_notifications: 'read',
    settings_security: 'full',
    settings_email: 'none',
    settings_users: 'none',
    settings_roles_permissions: 'none',
  }
}

// Helper para verificar si un usuario tiene permiso
export function hasPermission(
  userRole: UserRole,
  module: keyof ModulePermissions,
  requiredLevel: PermissionLevel = 'read',
): boolean {
  const permissions = getRolePermissions(userRole)
  const userLevel = permissions[module]

  if (userLevel === 'none') return false
  if (userLevel === 'full') return true
  if (requiredLevel === 'read' && (userLevel === 'read' || userLevel === 'own'))
    return true
  if (requiredLevel === 'own' && userLevel === 'own') return true

  return false
}
